import { useState, useEffect, useCallback } from 'react';
import { evento } from '@/lib/evento';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Store, Package, MessageSquare, Rocket,
  ChevronRight, ChevronLeft, Plus, Trash2,
  Check, Sparkles, ArrowRight, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.trim() || 'https://api.cotizaexpress.com';
const API = `${BACKEND_URL}/api`;

const GIROS = [
  'Ferretería',
  'Materiales de construcción',
  'Plomería',
  'Material eléctrico',
  'Pinturas',
  'Herrería',
  'Refaccionaria',
  'Distribuidora',
  'Mueblería',
  'Papelería',
  'Abarrotes / Mayoreo',
  'Servicios técnicos',
  'Otro',
];

// GA4: eventos del funnel de onboarding
const WIZ_SERVIDOR = { wizard_paso: null, wizard_salto_cotizador: 'wizard_salto_cotizador', wizard_wa_intento: 'wizard_whatsapp' };
const track = (ev, extra = {}) => {
  try { window.gtag && window.gtag('event', ev, extra); } catch (e) {}
  let srv = WIZ_SERVIDOR[ev];
  if (ev === 'wizard_paso') srv = ({ 2: 'wizard_productos', 3: 'wizard_whatsapp' })[extra.paso] || null;
  if (srv) evento(srv);
};

const GIRO_EMOJI = {
  'Ferretería': '🔨', 'Materiales de construcción': '🧱', 'Plomería': '🚰',
  'Material eléctrico': '💡', 'Pinturas': '🎨', 'Herrería': '⚒️',
  'Refaccionaria': '🚗', 'Distribuidora': '📦', 'Mueblería': '🛋️',
  'Papelería': '✏️', 'Abarrotes / Mayoreo': '🛒', 'Servicios técnicos': '🔧', 'Otro': '✨',
};

const EJEMPLOS_GIRO = {
  'Ferretería': [{ nombre: 'Cemento gris 50 kg', precio: '245' }, { nombre: 'Varilla 3/8 (pieza)', precio: '168' }, { nombre: 'Block 15x20x40', precio: '18.50' }],
  'Materiales de construcción': [{ nombre: 'Cemento gris 50 kg', precio: '245' }, { nombre: 'Arena (m3)', precio: '420' }, { nombre: 'Block 15x20x40', precio: '18.50' }],
  'Plomería': [{ nombre: 'Tubo PVC hidráulico 1/2" (tramo 6m)', precio: '95' }, { nombre: 'Llave de nariz 1/2"', precio: '85' }, { nombre: 'Codo cobre 1/2"', precio: '14' }],
  'Material eléctrico': [{ nombre: 'Cable THW cal 12 (rollo 100m)', precio: '1150' }, { nombre: 'Contacto dúplex', precio: '38' }, { nombre: 'Pastilla termomagnética 1x20A', precio: '145' }],
  'Pinturas': [{ nombre: 'Pintura vinílica blanca 19L', precio: '1290' }, { nombre: 'Sellador 5x1 (19L)', precio: '690' }, { nombre: 'Rodillo con maneral', precio: '85' }],
  'Herrería': [{ nombre: 'PTR 2x2 cal 14 (tramo 6m)', precio: '485' }, { nombre: 'Electrodo 6013 (kg)', precio: '95' }, { nombre: 'Disco de corte 4.5"', precio: '32' }],
  'Refaccionaria': [{ nombre: 'Balatas delanteras (juego)', precio: '650' }, { nombre: 'Filtro de aceite', precio: '120' }, { nombre: 'Bujía', precio: '95' }],
  'Distribuidora': [{ nombre: 'Caja producto A (12 pzas)', precio: '480' }, { nombre: 'Caja producto B (24 pzas)', precio: '720' }, { nombre: 'Pieza suelta', precio: '45' }],
  'Mueblería': [{ nombre: 'Colchón matrimonial', precio: '4990' }, { nombre: 'Sala 3 piezas', precio: '12900' }, { nombre: 'Mesa de centro', precio: '1890' }],
  'Papelería': [{ nombre: 'Resma papel carta (500 hojas)', precio: '135' }, { nombre: 'Caja bolígrafos (12 pzas)', precio: '85' }, { nombre: 'Cuaderno profesional 100 hojas', precio: '32' }],
  'Abarrotes / Mayoreo': [{ nombre: 'Caja aceite 1L (12 pzas)', precio: '540' }, { nombre: 'Bulto azúcar 25 kg', precio: '620' }, { nombre: 'Caja refresco 600ml (24 pzas)', precio: '310' }],
  'Servicios técnicos': [{ nombre: 'Visita de diagnóstico', precio: '350' }, { nombre: 'Mano de obra (hora)', precio: '450' }, { nombre: 'Servicio de mantenimiento', precio: '850' }],
  'Otro': [{ nombre: 'Producto o servicio 1', precio: '100' }, { nombre: 'Producto o servicio 2', precio: '250' }, { nombre: 'Producto o servicio 3', precio: '500' }],
};

const CIUDADES_US = [
  'Houston, TX', 'Dallas, TX', 'Austin, TX', 'San Antonio, TX', 'Fort Worth, TX', 'El Paso, TX', 'McAllen, TX', 'Laredo, TX',
  'Los Angeles, CA', 'San Diego, CA', 'San Jose, CA', 'Fresno, CA', 'Sacramento, CA', 'Bakersfield, CA', 'Riverside, CA', 'Santa Ana, CA',
  'Phoenix, AZ', 'Tucson, AZ', 'Chicago, IL', 'Aurora, IL', 'Denver, CO', 'Las Vegas, NV', 'Reno, NV',
  'Miami, FL', 'Orlando, FL', 'Tampa, FL', 'Jacksonville, FL', 'Atlanta, GA', 'Charlotte, NC', 'Raleigh, NC',
  'Nashville, TN', 'Memphis, TN', 'Oklahoma City, OK', 'Tulsa, OK', 'Kansas City, MO', 'Seattle, WA', 'Portland, OR',
  'Salt Lake City, UT', 'Albuquerque, NM', 'New York, NY', 'Newark, NJ', 'Indianapolis, IN', 'Columbus, OH', 'Milwaukee, WI',
];

const HORAS_DIA = [];
for (let h = 6; h <= 22; h++) { HORAS_DIA.push(`${String(h).padStart(2, '0')}:00`); if (h < 22) HORAS_DIA.push(`${String(h).padStart(2, '0')}:30`); }

function HorarioDia({ etiqueta, valor, onChange }) {
  const cerrado = (valor || '').toLowerCase() === 'cerrado';
  const partes = cerrado ? ['08:00', '18:00'] : (valor || '08:00-18:00').split('-');
  const abre = partes[0] || '08:00';
  const cierra = partes[1] || '18:00';
  return (
    <div className="bg-white rounded-xl shadow-sm p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-600">{etiqueta}</span>
        <button
          type="button"
          onClick={() => onChange(cerrado ? `${abre}-${cierra}` : 'cerrado')}
          className={`text-[11px] font-bold rounded-full px-2.5 py-1 border transition-all ${cerrado ? 'bg-slate-200 text-slate-600 border-slate-300' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
        >
          {cerrado ? '🔒 Cerrado' : '✅ Abierto'}
        </button>
      </div>
      {!cerrado && (
        <div className="flex items-center gap-1.5">
          <select value={abre} onChange={(e) => onChange(`${e.target.value}-${cierra}`)} className="flex-1 text-sm border border-slate-200 rounded-lg px-1.5 py-2 bg-slate-50">
            {HORAS_DIA.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
          <span className="text-slate-400 text-xs">a</span>
          <select value={cierra} onChange={(e) => onChange(`${abre}-${e.target.value}`)} className="flex-1 text-sm border border-slate-200 rounded-lg px-1.5 py-2 bg-slate-50">
            {HORAS_DIA.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

const CIUDADES_MX = [
  'Aguascalientes, Ags.', 'Mexicali, B.C.', 'Tijuana, B.C.', 'Ensenada, B.C.', 'La Paz, B.C.S.',
  'Campeche, Camp.', 'Tuxtla Gutiérrez, Chis.', 'Tapachula, Chis.', 'Chihuahua, Chih.', 'Ciudad Juárez, Chih.',
  'Ciudad de México', 'Saltillo, Coah.', 'Torreón, Coah.', 'Monclova, Coah.', 'Piedras Negras, Coah.',
  'Colima, Col.', 'Manzanillo, Col.', 'Durango, Dgo.', 'Gómez Palacio, Dgo.', 'León, Gto.',
  'Irapuato, Gto.', 'Celaya, Gto.', 'Guanajuato, Gto.', 'Acapulco, Gro.', 'Chilpancingo, Gro.',
  'Pachuca, Hgo.', 'Guadalajara, Jal.', 'Zapopan, Jal.', 'Puerto Vallarta, Jal.', 'Toluca, Edo. Méx.',
  'Naucalpan, Edo. Méx.', 'Ecatepec, Edo. Méx.', 'Morelia, Mich.', 'Uruapan, Mich.', 'Cuernavaca, Mor.',
  'Tepic, Nay.', 'Monterrey, N.L.', 'Guadalupe, N.L.', 'San Nicolás, N.L.', 'Apodaca, N.L.',
  'San Pedro Garza García, N.L.', 'Oaxaca, Oax.', 'Puebla, Pue.', 'Tehuacán, Pue.', 'Querétaro, Qro.',
  'Cancún, Q.R.', 'Playa del Carmen, Q.R.', 'Chetumal, Q.R.', 'San Luis Potosí, S.L.P.', 'Culiacán, Sin.',
  'Mazatlán, Sin.', 'Los Mochis, Sin.', 'Hermosillo, Son.', 'Ciudad Obregón, Son.', 'Nogales, Son.',
  'Villahermosa, Tab.', 'Ciudad Victoria, Tamps.', 'Tampico, Tamps.', 'Reynosa, Tamps.', 'Matamoros, Tamps.',
  'Nuevo Laredo, Tamps.', 'Ciudad Madero, Tamps.', 'Altamira, Tamps.', 'Tlaxcala, Tlax.', 'Veracruz, Ver.',
  'Xalapa, Ver.', 'Coatzacoalcos, Ver.', 'Poza Rica, Ver.', 'Córdoba, Ver.', 'Mérida, Yuc.', 'Zacatecas, Zac.',
];

const STEPS = [
  { icon: Store, title: 'Tu Negocio', desc: 'Cuéntanos sobre tu empresa' },
  { icon: Package, title: 'Tus Productos', desc: 'Agrega tus primeros productos' },
  { icon: MessageSquare, title: 'WhatsApp', desc: 'Conecta tu numero' },
  { icon: Rocket, title: '¡Listo!', desc: 'Activa tu CotizaBot' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);

  useEffect(() => { track('wizard_inicio'); }, []);
  const [saving, setSaving] = useState(false);

  // Step 1: Business info
  const [businessData, setBusinessData] = useState({
    giro: '',
    ciudad: '',
    descripcion: '',
    whatsapp: '',
    horario_semana: '08:00-18:00',
    horario_sabado: '08:00-14:00',
    horario_domingo: 'cerrado',
    pais: localStorage.getItem('cotizador_region') === 'US' ? 'US' : 'MX',
  });

  // WhatsApp Embedded Signup state
  const [waConnected, setWaConnected] = useState(false);
  const [waConnecting, setWaConnecting] = useState(false);
  const [waPhone, setWaPhone] = useState('');
  const [fbReady, setFbReady] = useState(false);

  // Load Facebook SDK
  useEffect(() => {
    if (document.getElementById('facebook-jssdk')) {
      if (window.FB) setFbReady(true);
      return;
    }
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: '1461694011992339',
        cookie: true,
        xfbml: true,
        version: 'v21.0',
      });
      setFbReady(true);
    };
    const js = document.createElement('script');
    js.id = 'facebook-jssdk';
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.async = true;
    js.defer = true;
    document.body.appendChild(js);
  }, []);

  // Listen for Embedded Signup session info
  const handleWAMessage = useCallback((event) => {
    if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        if (data.event === 'FINISH') {
          const { phone_number_id, waba_id } = data.data;
          console.log('WA Embedded Signup finished:', { phone_number_id, waba_id });
          // Store for use in the token exchange
          window.__wa_signup_data = { phone_number_id, waba_id };
        } else if (data.event === 'CANCEL') {
          console.log('WA Embedded Signup cancelled');
          setWaConnecting(false);
        } else if (data.event === 'ERROR') {
          console.error('WA Embedded Signup error:', data.data);
          toast.error('Error al conectar WhatsApp. Intenta de nuevo.');
          setWaConnecting(false);
        }
      }
    } catch (e) {
      // Not a JSON message, ignore
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleWAMessage);
    return () => window.removeEventListener('message', handleWAMessage);
  }, [handleWAMessage]);

  const launchWhatsAppSignup = (coexistencia = false) => {
    if (!fbReady || !window.FB) {
      toast.error('Cargando Facebook SDK, intenta en un momento...');
      return;
    }
    track('wizard_wa_intento', { modo: coexistencia ? 'coexistencia' : 'nuevo' });
    setWaConnecting(true);
    window.__wa_signup_data = null;

    window.FB.login(
      function (response) {
        if (response.authResponse) {
          const code = response.authResponse.code;
          const signupData = window.__wa_signup_data || {};
          // Send code + signup data to our backend
          axios.post(`${API}/whatsapp/embedded-signup`, {
            code,
            phone_number_id: signupData.phone_number_id,
            waba_id: signupData.waba_id,
          }, { withCredentials: true })
            .then((res) => {
              setWaConnected(true);
              setWaPhone(res.data?.phone_display || '');
              track('wizard_wa_conectado');
              toast.success('¡WhatsApp conectado exitosamente!');
            })
            .catch((err) => {
              console.error('Embedded signup backend error:', err);
              track('wizard_wa_error');
              toast.error(err?.response?.data?.detail || 'Error al conectar WhatsApp');
            })
            .finally(() => setWaConnecting(false));
        } else {
          console.log('User cancelled login');
          track('wizard_wa_cancelado');
          toast.info('Conexión cancelada. Si te atoraste, revisa los problemas comunes aquí abajo o escríbenos — te ayudamos por videollamada en 15 minutos.');
          setWaConnecting(false);
        }
      },
      {
        config_id: '1877916580262483',
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: coexistencia ? 'whatsapp_business_app_onboarding' : '',
          sessionInfoVersion: 3,
        },
      }
    );
  };

  // Step 2: Products
  const [products, setProducts] = useState([
    { nombre: '', precio: '' },
    { nombre: '', precio: '' },
    { nombre: '', precio: '' },
  ]);

  const addProduct = () => {
    if (products.length < 10) {
      setProducts([...products, { nombre: '', precio: '' }]);
    }
  };

  const removeProduct = (index) => {
    if (products.length > 1) {
      setProducts(products.filter((_, i) => i !== index));
    }
  };

  const updateProduct = (index, field, value) => {
    const updated = [...products];
    updated[index] = { ...updated[index], [field]: value };
    setProducts(updated);
  };

  const validProducts = products.filter(p => p.nombre.trim() && p.precio);

  // Step 1 → save business info
  const saveBusiness = async () => {
    if (!businessData.giro) {
      toast.error('Selecciona el giro de tu negocio');
      return false;
    }
    if (!businessData.whatsapp || businessData.whatsapp.length < 10) {
      toast.error('Ingresa tu número de WhatsApp (10 dígitos)');
      return false;
    }
    try {
      setSaving(true);
      await axios.put(`${API}/empresa/perfil`, {
        giro: businessData.giro,
        ciudad: businessData.ciudad,
        descripcion: businessData.descripcion,
        telefono: businessData.whatsapp,
        horario_semana: businessData.horario_semana,
        horario_sabado: businessData.horario_sabado,
        horario_domingo: businessData.horario_domingo,
      }, { withCredentials: true });
      return true;
    } catch (err) {
      // Silently continue if endpoint doesn't exist yet
      console.warn('Business save:', err?.response?.status);
      return true;
    } finally {
      setSaving(false);
    }
  };

  // Step 2 → save products
  const saveProducts = async () => {
    if (validProducts.length === 0) {
      toast.error('Agrega al menos un producto con nombre y precio');
      return false;
    }
    try {
      setSaving(true);
      const items = validProducts.map(p => ({
        name: p.nombre.trim(),
        price: parseFloat(p.precio),
        unit: 'Pieza',
        category: 'General',
      }));
      await axios.post(`${API}/pricebook/bulk`, { items }, { withCredentials: true });
      toast.success(`${items.length} producto(s) guardados`);
      return true;
    } catch (err) {
      console.warn('Products save:', err?.response?.status);
      toast.error('Error al guardar productos, pero puedes continuar');
      return true;
    } finally {
      setSaving(false);
    }
  };

  // Complete onboarding
  const completeOnboarding = async () => {
    try {
      await axios.post(`${API}/empresa/onboarding-complete`, {}, { withCredentials: true });
    } catch (err) {
      console.warn('Onboarding complete:', err?.response?.status);
    }
    track('wizard_completado');
    toast.success('¡Bienvenido a CotizaBot!');
    navigate('/dashboard');
  };

  const nextStep = async () => {
    if (step === 0) {
      const ok = await saveBusiness();
      if (!ok) return;
    }
    if (step === 1) {
      const ok = await saveProducts();
      if (!ok) return;
    }
    if (step < STEPS.length - 1) {
      track('wizard_paso', { paso: step + 2 });
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const empresaNombre = user?.empresa?.nombre || user?.usuario?.empresa_nombre || 'tu negocio';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-xl shadow-md shadow-emerald-200">🤖</div>
            <span className="font-extrabold text-lg bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">CotizaBot</span>
          </div>
          <span className="text-sm text-slate-500">Configuración inicial</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress bar */}
        <div className="flex items-center justify-center mb-8">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                i === step
                  ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300'
                  : i < step
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-100 text-slate-400'
              }`}>
                {i < step ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <s.icon className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{s.title}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 sm:w-12 h-0.5 mx-1 ${
                  i < step ? 'bg-emerald-500' : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6 sm:p-8">

            {/* ── STEP 0: Business Info ── */}
            {step === 0 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Store className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    ¡Hola, {user?.usuario?.nombre?.split(' ')[0] || 'bienvenido'}!
                  </h2>
                  <p className="text-slate-600 mt-1">
                    Cuéntanos sobre <strong>{empresaNombre}</strong> para configurar tu bot.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    ¿A qué se dedica tu negocio? *
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {GIROS.map(g => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setBusinessData({ ...businessData, giro: g })}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-2xl text-sm font-semibold border-2 transition-all ${
                          businessData.giro === g
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-md shadow-emerald-100 scale-[1.02]'
                            : 'border-slate-100 bg-white text-slate-600 hover:border-emerald-200 hover:bg-emerald-50/30'
                        }`}
                      >
                        <span className="text-xl">{GIRO_EMOJI[g] || '🏪'}</span>
                        <span className="text-left leading-tight">{g}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold text-slate-800">
                      📍 ¿En qué ciudad estás?
                    </label>
                    <div className="flex rounded-full border border-slate-200 overflow-hidden bg-white">
                      <button type="button" onClick={() => { setBusinessData({ ...businessData, pais: 'MX' }); localStorage.setItem('cotizador_region', 'MX'); }} className={`px-3 py-1 text-xs font-bold ${businessData.pais !== 'US' ? 'bg-emerald-600 text-white' : 'text-slate-500'}`}>🇲🇽 México</button>
                      <button type="button" onClick={() => { setBusinessData({ ...businessData, pais: 'US' }); localStorage.setItem('cotizador_region', 'US'); }} className={`px-3 py-1 text-xs font-bold ${businessData.pais === 'US' ? 'bg-blue-600 text-white' : 'text-slate-500'}`}>🇺🇸 USA</button>
                    </div>
                  </div>
                  <input
                    type="text"
                    list={businessData.pais === 'US' ? 'ciudades-us' : 'ciudades-mx'}
                    value={businessData.ciudad}
                    onChange={(e) => setBusinessData({ ...businessData, ciudad: e.target.value })}
                    className="w-full px-4 py-3 border-0 bg-white rounded-xl shadow-sm focus:ring-2 focus:ring-emerald-400 text-base"
                    placeholder={businessData.pais === 'US' ? 'Empieza a escribir: Hou...' : 'Empieza a escribir: Monte...'}
                  />
                  <datalist id="ciudades-mx">
                    {CIUDADES_MX.map((c) => <option key={c} value={c} />)}
                  </datalist>
                  <datalist id="ciudades-us">
                    {CIUDADES_US.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>

                <div className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-4">
                  <label className="block text-sm font-bold text-slate-800 mb-1">
                    💬 El WhatsApp donde te escriben tus clientes *
                  </label>
                  <p className="text-xs text-slate-500 mb-3">Solo para conocerlo — no se conecta nada todavía.</p>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-emerald-700 bg-white px-4 py-3 rounded-xl shadow-sm">{businessData.pais === 'US' ? '🇺🇸 +1' : '🇲🇽 +52'}</span>
                    <input
                      type="tel"
                      value={businessData.whatsapp}
                      onChange={(e) => setBusinessData({ ...businessData, whatsapp: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      className="flex-1 px-4 py-3 border-0 bg-white rounded-xl shadow-sm focus:ring-2 focus:ring-emerald-400 text-base tracking-wider"
                      placeholder={businessData.pais === 'US' ? '512 123 4567' : '81 1234 5678'}
                      maxLength={10}
                    />
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-4">
                  <label className="block text-sm font-bold text-slate-800 mb-3">
                    🕗 ¿A qué hora atiendes?
                  </label>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <HorarioDia etiqueta="💼 Lun - Vie" valor={businessData.horario_semana} onChange={(v) => setBusinessData({ ...businessData, horario_semana: v })} />
                    <HorarioDia etiqueta="🛠️ Sábado" valor={businessData.horario_sabado} onChange={(v) => setBusinessData({ ...businessData, horario_sabado: v })} />
                    <HorarioDia etiqueta="🏖️ Domingo" valor={businessData.horario_domingo} onChange={(v) => setBusinessData({ ...businessData, horario_domingo: v })} />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Solo pícale — nada que teclear. El bot avisa estos horarios a tus clientes.</p>
                </div>
              </div>
            )}

            {/* ── STEP 1: Products ── */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900">¿Qué vendes? 🛒</h2>
                  <p className="text-slate-600 mt-2 text-lg">
                    Escribe <strong>3 productos</strong> — los que más te piden. Nada más.
                  </p>
                  <p className="text-sm text-slate-400 mt-1">El catálogo completo lo subes después con tu Excel (1 clic).</p>
                  <button
                    type="button"
                    onClick={() => {
                      setProducts((EJEMPLOS_GIRO[businessData.giro] || EJEMPLOS_GIRO['Otro']).map(x => ({ ...x })));
                      track('wizard_productos_ejemplo');
                      toast.success('Listo: 3 productos de ejemplo. Puedes cambiarlos o seguir.');
                    }}
                    className="mt-4 inline-flex items-center gap-2 bg-violet-50 border-2 border-violet-200 text-violet-700 font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-violet-100 hover:border-violet-300 transition-all"
                  >
                    ⚡ ¿Con prisa? Llénalo con productos de ejemplo
                  </button>
                </div>

                <div className="space-y-3 max-w-xl mx-auto">
                  {products.map((p, i) => {
                    const ejemplos = ['Ej: Cemento gris 50 kg', 'Ej: Varilla 3/8', 'Ej: Block 15x20x40', 'Ej: Pintura vinílica 19L', 'Ej: Lámina galvanizada'];
                    const colores = ['bg-emerald-500', 'bg-teal-500', 'bg-cyan-500', 'bg-violet-500', 'bg-pink-500'];
                    return (
                      <div key={i} className="flex items-center gap-3 bg-white border-2 border-slate-100 hover:border-emerald-200 rounded-2xl p-3 shadow-sm transition-all">
                        <div className={`w-9 h-9 ${colores[i % colores.length]} text-white rounded-full flex items-center justify-center font-bold flex-shrink-0`}>
                          {i + 1}
                        </div>
                        <input
                          type="text"
                          value={p.nombre}
                          onChange={(e) => updateProduct(i, 'nombre', e.target.value)}
                          className="flex-1 px-3 py-3 border-0 bg-slate-50 rounded-xl focus:ring-2 focus:ring-emerald-400 text-base"
                          placeholder={ejemplos[i % ejemplos.length]}
                        />
                        <div className="relative w-32 flex-shrink-0">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-bold">$</span>
                          <input
                            type="number"
                            value={p.precio}
                            onChange={(e) => updateProduct(i, 'precio', e.target.value)}
                            className="w-full pl-7 pr-2 py-3 border-0 bg-emerald-50 rounded-xl focus:ring-2 focus:ring-emerald-400 text-base font-semibold text-slate-800"
                            placeholder="precio"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeProduct(i)}
                          className="p-2 text-slate-300 hover:text-red-500 transition-colors"
                          disabled={products.length <= 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {products.length < 10 && (
                  <button
                    type="button"
                    onClick={addProduct}
                    className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    <Plus className="w-4 h-4" /> Agregar otro producto
                  </button>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                  <strong>Tip:</strong> No necesitas agregar todo tu catálogo ahora.
                  Puedes cargar productos por Excel después desde el dashboard.
                </div>
              </div>
            )}

            {/* ── STEP 2: Connect WhatsApp ── */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Conecta tu WhatsApp</h2>
                  <p className="text-slate-600 mt-1">
                    Vincula tu número de WhatsApp Business para que el bot empiece a responder.
                  </p>
                </div>

                {waConnected ? (
                  <div className="text-center space-y-4">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                      <Check className="w-10 h-10 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-green-700">WhatsApp conectado</h3>
                      {waPhone && <p className="text-slate-600 mt-1">{waPhone}</p>}
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                      <Sparkles className="w-4 h-4 inline mr-1" />
                      Tu bot ya tiene los <strong>{validProducts.length || 0} productos</strong> y est&aacute; listo para recibir mensajes.
                    </div>
                    <div>
                      <Button
                        variant="outline"
                        onClick={launchWhatsAppSignup}
                        disabled={waConnecting}
                        className="mt-2"
                      >
                        {waConnecting ? 'Conectando...' : 'Conectar otro número'}
                      </Button>
                      <p className="text-xs text-slate-400 mt-2">
                        ¿Conectaste un número de prueba (+1 555...) o quieres cambiarlo? Vuelve a hacer la conexión con el número correcto.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-6">
                    {/* Elige tu número: UNA pregunta, DOS tarjetas */}
                    <p className="text-sm text-slate-500">Ten a la mano tu Facebook y tu teléfono. Toma 2 minutos.</p>

                    {/* OPCIÓN PRINCIPAL: entrada ligera */}
                    <div className="max-w-2xl mx-auto bg-gradient-to-br from-violet-600 to-purple-600 rounded-3xl p-6 sm:p-8 text-white shadow-xl shadow-violet-200">
                      <p className="text-2xl font-extrabold">✨ Empieza haciendo tus cotizaciones</p>
                      <p className="text-violet-100 mt-2 mb-5">
                        Pega la lista de tu cliente y la IA te la cotiza — con IVA, folio y PDF. Sin conectar nada. Tu primera es gratis.
                      </p>
                      <button
                        type="button"
                        onClick={() => { track('wizard_salto_cotizador'); navigate('/cotizador'); }}
                        className="w-full sm:w-auto bg-white text-violet-700 font-extrabold text-lg rounded-full px-8 py-4 hover:bg-violet-50 transition-all shadow-lg"
                      >
                        Hacer mi primera cotización →
                      </button>
                    </div>

                    <div className="flex items-center gap-3 max-w-2xl mx-auto">
                      <div className="flex-1 h-px bg-slate-200"></div>
                      <p className="text-sm text-slate-400">¿O quieres el paquete completo?</p>
                      <div className="flex-1 h-px bg-slate-200"></div>
                    </div>

                    <p className="text-lg font-bold text-slate-900">🤖 Que el bot responda y cobre solo en tu WhatsApp</p>
                    <p className="text-sm text-slate-500 -mt-4">Elige qué número va a usar:</p>

                    <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
                      {/* Opción A: coexistencia */}
                      <div className="border-2 border-emerald-400 bg-emerald-50/60 rounded-xl p-5 flex flex-col relative">
                        <span className="absolute -top-3 left-4 bg-emerald-600 text-white text-xs font-bold px-3 py-0.5 rounded-full">Recomendado</span>
                        <p className="font-bold text-slate-900 mt-1">✨ Mi número de siempre</p>
                        <p className="text-sm text-slate-600 mt-1 flex-1">
                          <strong>Todo lo sigues viendo en tu celular, como siempre.</strong> El bot contesta solo y sus respuestas aparecen en tu WhatsApp; tú entras a cualquier chat cuando quieras.
                        </p>
                        <Button
                          type="button"
                          onClick={() => launchWhatsAppSignup(true)}
                          disabled={waConnecting}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold w-full mt-3"
                        >
                          {waConnecting ? 'Conectando...' : 'Conectar mi número ✅'}
                        </Button>
                        <p className="text-[11px] text-slate-400 mt-2">Funciona si ese número ya usa la app de WhatsApp Business (la verde) desde hace una semana o más.</p>
                      </div>

                      {/* Opción B: número dedicado */}
                      <div className="border border-slate-200 bg-white rounded-xl p-5 flex flex-col">
                        <p className="font-bold text-slate-900 mt-1">📞 Otro número (nuevo o fijo)</p>
                        <p className="text-sm text-slate-600 mt-1 flex-1">
                          Un número solo para el bot: un chip nuevo o el teléfono fijo del negocio. Ese número quedará dedicado al bot.
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => launchWhatsAppSignup(false)}
                          disabled={waConnecting}
                          className="w-full mt-3 border-2"
                        >
                          {waConnecting ? 'Conectando...' : 'Conectar otro número'}
                        </Button>
                        <p className="text-[11px] text-slate-400 mt-2">Si es fijo, en la verificación elige "Llamarme": una grabación te dicta el código.</p>
                      </div>
                    </div>

                    {/* Qué va a pasar (colapsado) */}
                    <details className="max-w-2xl mx-auto text-left text-sm bg-slate-50 rounded-lg px-4 py-3">
                      <summary className="cursor-pointer font-medium text-slate-700">¿Qué voy a ver al picar el botón? (son 4 pantallas)</summary>
                      <ol className="mt-2 space-y-1.5 text-slate-600 list-decimal list-inside">
                        <li>Se abre una ventana de Facebook: inicia sesión y cuando diga "CotizaExpress quiere acceder", pica <strong>Continuar</strong>.</li>
                        <li>Escribe el nombre de tu negocio.</li>
                        <li>Escribe el número que elegiste y captura el código que te llega (SMS) o te dictan ("Llamarme").</li>
                        <li>Se cierra la ventana y aquí verás <strong>"WhatsApp conectado" ✅</strong></li>
                      </ol>
                    </details>

                                        {/* Atorones comunes */}
                    <div className="text-left max-w-md mx-auto space-y-2">
                      <p className="text-sm font-semibold text-slate-700">¿Te atoraste? Los 3 problemas más comunes:</p>
                      {[
                        ['"Este número ya está registrado en WhatsApp"', 'Ese número tiene WhatsApp activo en un teléfono. Si quieres usar ese mismo número para el bot: en el teléfono ve a WhatsApp → Ajustes → Cuenta → Eliminar cuenta, espera 5 minutos e intenta de nuevo (tus chats se pierden, respáldalos antes si los necesitas). Si prefieres conservarlo como está, conecta otro número.'],
                        ['No se abre la ventana de Facebook', 'Tu navegador bloqueó la ventana emergente. Busca el aviso de "popup bloqueado" en la barra de dirección y permítelo, o intenta desde Chrome.'],
                        ['No llega el código SMS', 'En la pantalla de verificación elige la opción "Llamarme" — a los números fijos siempre se les verifica por llamada. Ten el teléfono a la mano: la grabación dicta el código una vez.'],
                        ['Uso WhatsApp normal, no el Business — ¿puedo?', 'Para conectar tu número de siempre necesitas la app de WhatsApp Business (la verde, gratis). Descárgala, migra tu número ahí (conservas tus chats, toma 10 minutos) y úsala unos 7 días. Después regresas aquí y conectas. Si no quieres esperar, conecta mientras tanto otro número (opción de la derecha).'],
                        ['¿Es seguro conectar mi Facebook?', 'Sí. Facebook solo se usa para autorizar la conexión de tu WhatsApp — no publicamos nada ni vemos tu cuenta personal. Cuando pida permiso para "CotizaExpress", pica Continuar.'],
                      ].map(([q, a], i) => (
                        <details key={i} className="bg-white border border-slate-200 rounded-lg px-4 py-2">
                          <summary className="text-sm font-medium text-slate-800 cursor-pointer">{q}</summary>
                          <p className="text-xs text-slate-600 mt-2">{a}</p>
                        </details>
                      ))}
                    </div>


                    {/* Help CTA - Task #22 */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4 text-left">
                      <p className="text-sm font-medium text-amber-900 mb-1">¿Necesitas ayuda con este paso?</p>
                      <p className="text-xs text-amber-700 mb-3">
                        Nuestro equipo te guía por videollamada para configurar tu WhatsApp en 15 minutos.
                      </p>
                      <a
                        href="https://wa.me/5218342472640?text=Hola%2C%20necesito%20ayuda%20configurando%20mi%20WhatsApp%20en%20CotizaBot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.237 0-4.308-.744-5.977-1.998l-.418-.312-3.087 1.034 1.034-3.087-.312-.418A9.935 9.935 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                        </svg>
                        Escríbenos por WhatsApp
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 3: Ready! ── */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Rocket className="w-8 h-8 text-amber-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">¡Todo listo!</h2>
                  <p className="text-slate-600 mt-1">
                    Tu CotizaBot está configurado. Esto es lo que sigue:
                  </p>
                </div>

                <div className="space-y-4">
                  {[
                    {
                      num: '0',
                      title: 'Escríbele a tu bot AHORITA 🧪',
                      desc: 'Prueba cómo cotiza con tus productos — sin conectar nada. Entra a la pestaña "Probar mi bot".',
                      color: 'emerald',
                    },
                    {
                      num: '1',
                      title: waConnected ? 'WhatsApp conectado' : 'Conecta tu WhatsApp',
                      desc: waConnected
                        ? 'Tu bot ya esta recibiendo mensajes en tu numero de WhatsApp.'
                        : 'Puedes conectar tu WhatsApp desde el dashboard en cualquier momento.',
                      color: 'emerald',
                    },
                    {
                      num: '2',
                      title: 'Completa tu catalogo',
                      desc: 'Puedes agregar mas productos desde el dashboard, uno por uno o por archivo Excel.',
                      color: 'blue',
                    },
                    {
                      num: '3',
                      title: 'Tus clientes empiezan a cotizar',
                      desc: 'Comparte tu numero de WhatsApp y tus clientes podran pedir cotizaciones automaticas.',
                      color: 'purple',
                    },
                  ].map((item) => (
                    <div key={item.num} className="flex gap-4 items-start p-4 bg-slate-50 rounded-xl">
                      <div className={`w-8 h-8 bg-${item.color}-100 text-${item.color}-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0`}>
                        {item.num}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.title}</h3>
                        <p className="text-sm text-slate-600 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-6 text-center text-white">
                  <h3 className="text-lg font-bold mb-1">Todo listo</h3>
                  <p className="text-emerald-100 text-sm mb-4">
                    Tu bot de WhatsApp está configurado. Activa tu plan para empezar a recibir pedidos.
                  </p>
                  <Button
                    onClick={completeOnboarding}
                    size="lg"
                    className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-8"
                  >
                    Ir a mi Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            {step < 3 && (
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-100">
                <button
                  type="button"
                  onClick={prevStep}
                  className={`flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors ${
                    step === 0 ? 'invisible' : ''
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" /> Atrás
                </button>

                <div className="flex items-center gap-3">
                  {step < 2 && (
                    <button
                      type="button"
                      onClick={() => setStep(step + 1)}
                      className="text-sm text-slate-400 hover:text-slate-600"
                    >
                      Omitir
                    </button>
                  )}
                  <Button
                    onClick={nextStep}
                    disabled={saving}
                    className="px-6"
                  >
                    {saving ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Guardando...
                      </div>
                    ) : (
                      <span className="flex items-center gap-1">
                        Continuar <ChevronRight className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            )}

          </CardContent>
        </Card>
      </div>
    </div>
  );
}
